import React, {AriaAttributes} from 'react';
import classNames from 'classnames';
import {Breadcrumb} from '@udemy-v2/react-navigation-components';
import {CourseCategory, Topic} from '@udemy-v2/browse-course';
import styles from './breadcrumb-navigation.module.scss';

interface BreadcrumbNavigationProps {
    primaryCategory?: CourseCategory;
    primaryTopic?: Topic;
    ariaLabel?: AriaAttributes['aria-label'];
}

interface BreadcrumbItem {
    id: string;
    name: string;
    href: string;
}

/**
 * Displays a breadcrumb navigation from categories and additional items
 */
export function BreadcrumbNavigation({
    primaryCategory,
    primaryTopic,
    ariaLabel,
}: BreadcrumbNavigationProps) {
    // Get primary subcategory if available
    const primarySubcategory = primaryCategory?.subcategories?.[0];

    // Create array of breadcrumb items to render
    const breadcrumbItems: BreadcrumbItem[] = [];

    // Add category if available
    if (primaryCategory) {
        breadcrumbItems.push({
            id: primaryCategory.id,
            name: primaryCategory.name,
            href: primaryCategory.url,
        });
    }

    // Add subcategory if available
    if (primarySubcategory) {
        breadcrumbItems.push({
            id: primarySubcategory.id,
            name: primarySubcategory.name,
            href: primarySubcategory.url,
        });
    }

    // Add topic if available
    if (primaryTopic) {
        breadcrumbItems.push({
            id: primaryTopic.id,
            name: primaryTopic.name,
            href: primaryTopic.url,
        });
    }

    // Don't render anything if there are no breadcrumb items
    if (breadcrumbItems.length === 0) {
        return null;
    }

    // Hide the first item (primaryCategory) on mobile only when we have all 3 items
    const shouldHideFirstOnMobile = breadcrumbItems.length === 3;
    const breadcrumbClassName = classNames(
        styles['breadcrumb-container'],
        shouldHideFirstOnMobile && styles['hide-first-on-mobile'],
    );

    return (
        <Breadcrumb className={breadcrumbClassName} ariaLabel={ariaLabel}>
            {breadcrumbItems.map((item, index) => (
                <Breadcrumb.Item
                    key={item.id}
                    href={item.href}
                    ariaCurrent={index === breadcrumbItems.length - 1 ? true : undefined}
                >
                    {item.name}
                </Breadcrumb.Item>
            ))}
        </Breadcrumb>
    );
}
